import {useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  IconButton,
  InputAdornment,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import {AffiliatedUser} from '../../../../../../../../../backend/responses/affiliated-user';
import {translations} from '../../../../../../../../../i18n/translations';
import {useFetchEntityUsers} from '../../../../../../../../../hooks/use-fetch-entity-users';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import {UserAvatar} from '../../../shared/user-avatar';
import {useEntity} from '../../../../../../../../../contexts/entity/context';
import {getLocalizedFieldValue} from '../../../../../../../../../utils/functions/get-localized-field-value';

export type PlannerActivityUsersProps = {
  users?: AffiliatedUser[];
  disabled?: boolean;
  onChange: (users: AffiliatedUser[]) => void;
};

export function PlannerActivityUsers(props: PlannerActivityUsersProps) {
  const {items, isLoading} = useFetchEntityUsers({activeUsers: true});
  const {t} = useTranslation();
  const entityContext = useEntity();

  const options: SelectOptionValue[] | undefined = useMemo(() => {
    if (isLoading) return undefined;

    const selectAllUsersOption: SelectOptionValue = {
      id: 'select-all-users',
      name: t(translations.planner.modal.create.activity.add_users_mark_all),
      type: 'global',
    };

    const users =
      items?.map<SelectOptionValue>((item) => ({
        id: item.user.id!,
        name: item.user.name,
        picture: item.user.picture,
        socialResponses: item.affiliation.socialResponses,
        type: 'user',
      })) ?? [];

    const socialResponses = entityContext.state.userEntitySocialResponses.map<SelectOptionValue>(
      (socialResponse) => ({
        id: socialResponse.id!,
        name: t(translations.planner.modal.create.activity.add_users_mark, {
          sr: getLocalizedFieldValue(socialResponse, 'abbreviation'),
          count:
            items?.filter((item) => item.affiliation?.socialResponses?.includes(socialResponse.id))
              .length ?? 0,
        }),
        type: 'social_response',
      })
    );

    return [selectAllUsersOption, ...socialResponses, ...users];
  }, [items, t, entityContext.state.userEntitySocialResponses, isLoading]);

  const selectedOptions: SelectOptionValue[] | undefined = useMemo(() => {
    return props.users?.map<SelectOptionValue>((user) => ({
      id: user.user.id!,
      name: user.user.name,
      picture: user.user.picture,
      socialResponses: user.affiliation.socialResponses,
      type: 'user',
    }));
  }, [props.users]);

  return (
    <UsersMultySearchSelectInput
      options={options}
      selectedOptions={selectedOptions}
      disabled={props.disabled}
      onChange={(values) => {
        const users: AffiliatedUser[] = values.map((value) => {
          return items?.find((item) => item.user.id === value.id)!;
        });

        props.onChange(users);
      }}
    />
  );
}

// ---------------------------------------------------------------------------------
// ---------------------------------------------------------------------------------
// ---------------------------------------------------------------------------------

export type SelectOptionValue = {
  id: string;
  name: string;
  picture?: string;
  socialResponses?: string[];
  type: 'global' | 'user' | 'social_response';
};

type UsersMultySearchSelectInputProps = {
  options?: SelectOptionValue[];
  selectedOptions?: SelectOptionValue[];
  disabled?: boolean;
  onChange?: (values: SelectOptionValue[]) => void;
};

export function UsersMultySearchSelectInput(props: UsersMultySearchSelectInputProps) {
  const [selectedOptions, setSelectedOptions] = useState<SelectOptionValue[]>(
    props.selectedOptions ?? []
  );
  const selectedOptionsRef = useRef(selectedOptions);
  const {t} = useTranslation();

  const updateSelectedOptions = (values: SelectOptionValue[]) => {
    selectedOptionsRef.current = values;
    setSelectedOptions(values);
  };

  const removeSelectedUser = (userId: string) => {
    const values = selectedOptionsRef.current.filter(
      (option) => option.type !== 'user' || option.id !== userId
    );
    const selectedSocialResponseOptions = values.filter(
      (option) => option.type === 'social_response'
    );
    const selectedUsers = values.filter((option) => option.type === 'user');

    updateSelectedOptions([...selectedSocialResponseOptions, ...selectedUsers]);
    props.onChange?.(selectedUsers);
  };

  const selectedOptionsUsersOnly = useMemo(() => {
    // The selected options can be a mix of users and social responses
    // social responses are not meant to be displayed in the selected items list
    // since they are not users but rather a selection of users.
    return selectedOptions.filter((x) => x.type === 'user');
  }, [selectedOptions]);

  return (
    <>
      {/* ------------------- LABEL ------------------- */}
      <Stack flexDirection="row">
        <Typography
          noWrap
          sx={{
            fontWeight: 500,
            fontSize: '16px',
            color: 'gray.main',
            marginBottom: '4px',
          }}
        >
          {t(translations.planner.modal.create.activity.add_users_label)}
        </Typography>
        <Box component="span" sx={{color: 'error.main', paddingLeft: '5px', fontWeight: '500'}}>
          *
        </Box>
      </Stack>

      {/* ------------------- INPUT ------------------- */}
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={props.options ?? []}
        groupBy={(option) => {
          if (option.type === 'global') {
            return '';
          }
          return t(translations.planner.modal.create.activity.add_users_type[option.type]);
        }}
        value={selectedOptions}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <Input
            params={params}
            placeholder={t(translations.planner.modal.create.activity.add_users_placeholder)}
          />
        )}
        loading={props.options === undefined}
        loadingText={t(translations.loading_content.loading)}
        noOptionsText={t(translations.search_select_input.no_options)}
        disabled={props.disabled}
        renderOption={(props, option, state) => (
          <Option key={option.id} htmlProps={props} value={option} selected={state.selected} />
        )}
        onChange={(_, values, reason, details) => {
          // Add / Remove
          if (reason === 'selectOption' || reason === 'removeOption' || reason === 'clear') {
            const clickedValue = details?.option;
            const selectedSocialResponseOptions = values.filter(
              (value) => value.type === 'social_response'
            );
            const selectedUsers = values.filter((value) => value.type === 'user');

            if (clickedValue?.type === 'social_response') {
              if (reason === 'selectOption') {
                const users =
                  props.options?.filter((option) => {
                    if (option.type === 'social_response') return false;

                    return option.socialResponses?.some((socialResponse) =>
                      values.some(
                        (selectBySocialResponse) => selectBySocialResponse.id === socialResponse
                      )
                    );
                  }) ?? [];

                updateSelectedOptions([...selectedSocialResponseOptions, ...users]);
                props.onChange?.(users);
              }
              // removing
              else {
                const users =
                  selectedUsers?.filter((user) => {
                    return user.socialResponses?.some(
                      (socialResponse) =>
                        clickedValue.id !== socialResponse &&
                        selectedSocialResponseOptions.some((x) => x.id === socialResponse)
                    );
                  }) ?? [];

                updateSelectedOptions([...selectedSocialResponseOptions, ...users]);
                props.onChange?.(users);
              }
            } else if (clickedValue?.type === 'global' && clickedValue?.id === 'select-all-users') {
              if (reason === 'selectOption') {
                const selectedAllUsersOptions = values.filter(
                  (value) => value.type === 'global' && value.id === 'select-all-users'
                );
                const users =
                  props.options?.filter((option) => {
                    if (option.type === 'user') return true;
                    return false;
                  }) ?? [];

                updateSelectedOptions([...selectedAllUsersOptions, ...users]);
                props.onChange?.(users);
              } else {
                updateSelectedOptions([]);
                props.onChange?.([]);
              }
            } else {
              updateSelectedOptions([...selectedSocialResponseOptions, ...selectedUsers]);
              props.onChange?.(selectedUsers);
            }
          }
        }}
      />

      {/* --------------- SELECTED ITEMS --------------- */}
      <Box
        sx={{
          backgroundColor: selectedOptions.length > 0 ? 'blue.bg' : undefined,
          padding: '0 8px',
        }}
      >
        {/* ---------- SUB LABEL ---------- */}
        <Stack sx={{height: '32px'}} justifyContent="center">
          <Typography
            component="label"
            sx={{fontWeight: 500, fontSize: '14px', color: 'gray.main'}}
          >
            {t(translations.planner.modal.create.activity.added_users)} (
            {selectedOptionsUsersOnly.length})
          </Typography>
        </Stack>

        {/* ------------ ITEMS ------------ */}
        <Box sx={{height: '205px', overflow: 'auto'}}>
          <Box
            sx={{
              display: 'grid',
              width: 'max-content',
              maxWidth: '100%',
              gridTemplateColumns: '26px minmax(0, 1fr) max-content',
              gridAutoRows: '38px',
              columnGap: '4px',
              alignItems: 'center',
            }}
          >
            {selectedOptionsUsersOnly.map((option) => (
              <SelectedItem
                key={option.id}
                name={option.name}
                picture={option.picture}
                socialResponses={option.socialResponses}
                removeDisabled={props.disabled}
                onRemove={() => removeSelectedUser(option.id)}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );
}

// ---------------------------------------------------------------------------------

function Option(props: {
  htmlProps: React.HTMLAttributes<HTMLElement>;
  value: SelectOptionValue;
  selected: boolean;
}) {
  return (
    <Stack
      {...props.htmlProps}
      flexDirection="row"
      height="38px"
      padding="0 6px !important"
      gap="8px"
    >
      <Checkbox size="small" checked={props.selected} sx={{width: '12px', height: '12px'}} />
      {props.value.type === 'user' && (
        <UserAvatar
          size="s"
          disableTooltip
          name={props.value.name}
          picture={props.value.picture}
          socialResponses={props.value.socialResponses}
        />
      )}
      <span>{props.value.name}</span>
      <Box sx={{height: '28px', width: '28px', display: 'flex', marginLeft: 'auto'}}>
        {props.selected && (
          <SvgIcon sx={{fontSize: 'small', margin: 'auto', mr: '9px'}}>
            <CheckIcon />
          </SvgIcon>
        )}
      </Box>
    </Stack>
  );
}

function Input(props: {params: AutocompleteRenderInputParams; placeholder?: string}) {
  return (
    <TextField
      {...props.params}
      variant="filled"
      placeholder={props.placeholder}
      InputProps={{
        ...props.params.InputProps,
        startAdornment: (
          <InputAdornment position="start">
            <GroupOutlinedIcon />
          </InputAdornment>
        ),
      }}
      sx={{
        '& .MuiInputBase-root': {
          height: '48px',
          padding: '8px 12px',
          alignContent: 'center',
          gap: '6px',
        },
        '& .MuiInputBase-input': {
          color: 'gray.main',
        },
        '& .MuiInputAdornment-root': {
          margin: '0 !important',
          height: '100%',
        },
      }}
    />
  );
}

function SelectedItem(props: {
  name: string;
  picture?: string;
  socialResponses?: string[];
  removeDisabled?: boolean;
  onRemove?: () => void;
}) {
  const {t} = useTranslation();

  return (
    <Box sx={{display: 'contents'}}>
      <UserAvatar
        disableTooltip
        size="m"
        name={props.name}
        picture={props.picture}
        socialResponses={props.socialResponses}
      />
      <Typography
        noWrap
        sx={{
          color: 'gray.main',
          fontSize: '14px',
          fontWeight: 500,
          minWidth: 0,
          maxWidth: '240px',
        }}
      >
        {props.name}
      </Typography>
      <IconButton
        type="button"
        aria-label={t(translations.generic_header.actions.remove)}
        size="small"
        disabled={props.removeDisabled}
        onClick={props.onRemove}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
